import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionVideo from "@/components/MissionVideo";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import ChoiceCard from "@/components/ChoiceCard";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_MISSION1_PROBLEMS,
} from "@/utils/jrDemoState";
import { getMissionVideo } from "@/utils/jrMissionMedia";

/** Reverse-lookup the option key from a saved problem string so reopening
 *  the page restores the previous selection. */
function findChoiceFromSaved(saved) {
  if (!saved) return { key: "", custom: "" };
  const match = JR_MISSION1_PROBLEMS.find(
    (o) => o.key !== "custom" && o.label === saved
  );
  if (match) return { key: match.key, custom: "" };
  return { key: "custom", custom: saved };
}

export default function Mission1() {
  const navigate = useNavigate();
  const initial = findChoiceFromSaved(loadJrState().problem);
  const [choice, setChoice] = useState(initial.key);
  const [custom, setCustom] = useState(initial.custom);
  const [showOverlay, setShowOverlay] = useState(false);
  const video = getMissionVideo(1);

  const isCustom = choice === "custom";
  const canContinue = !!choice && (!isCustom || custom.trim().length >= 3);

  const onContinue = () => {
    if (!canContinue) return;
    const opt = JR_MISSION1_PROBLEMS.find((o) => o.key === choice);
    const problem = isCustom ? custom.trim() : opt ? opt.label : "";
    saveJrState({ problem });
    // 2 stars only if the kid wrote their own idea.
    markMissionComplete(1, { customized: isCustom });
    setShowOverlay(true);
  };

  return (
    <ElementaryMissionPage
      missionNumber={1}
      title="Welcome to CEO City"
      lesson="A business is an idea that helps people."
      canContinue={canContinue}
      onContinue={onContinue}
      testId="elementary-mission-1-page"
    >
      {video && (
        <MissionVideo url={video.url} title={video.title} testId="mission-1-video" />
      )}
      <div className="mb-6">
        <LemonBotJr
          greeting="Junior CEOs solve problems. Tap the one that matters most to you!"
          prompts={[
            "There is no wrong answer.",
            "Pick a problem you really care about.",
          ]}
          compact
        />
      </div>

      <p className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
        Which problem do you want to help solve?
      </p>

      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
        {JR_MISSION1_PROBLEMS.map((opt) => (
          <ChoiceCard
            key={opt.key}
            icon={opt.icon}
            label={opt.label}
            color={opt.color}
            selected={choice === opt.key}
            onSelect={() => setChoice(opt.key)}
            testId={`mission-1-option-${opt.key}`}
          />
        ))}
      </div>

      {isCustom && (
        <div className="mt-5">
          <label className="block text-sm font-bold text-gray-900 mb-2">
            Tell us your idea
          </label>
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            maxLength={160}
            placeholder="A problem I notice is…"
            className="w-full px-4 py-3 text-lg border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
            data-testid="mission-1-custom-input"
            autoFocus
          />
        </div>
      )}

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={1}
          missionTitle="Welcome to CEO City"
          message="Great work, Junior CEO! Now let's meet your team — the Biz Birds."
          continueLabel="On to Mission 2"
          onContinue={() => navigate("/elementary/demo/mission/2")}
        />
      )}
    </ElementaryMissionPage>
  );
}
